import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { CalendarIcon, SearchIcon, ArrowRightIcon, TagIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  categoryColor: string;
  categoryBg: string;
  emoji: string;
  gradientFrom: string;
  gradientTo: string;
}
const categories = [{
  label: 'Усі',
  value: 'all'
}, {
  label: 'Кейси',
  value: 'Кейс'
}, {
  label: 'Статті',
  value: 'Стаття'
}, {
  label: 'Огляди',
  value: 'Огляд'
}, {
  label: 'Гайди',
  value: 'Гайд'
}];
const articles: Article[] = [{
  id: '1',
  slug: '/blog/it-team-case',
  title: 'Як ми зібрали IT‑відділ із 15 людей за 6 тижнів',
  excerpt: 'Розповідаємо про стратегію масового найму для швидкозростаючого фінтех‑стартапу та результати через півроку.',
  date: '15 лютого 2026',
  category: 'Кейс',
  categoryColor: 'text-primary-600',
  categoryBg: 'bg-primary-50',
  emoji: '📊',
  gradientFrom: 'from-primary-100',
  gradientTo: 'to-cyan-100'
}, {
  id: '2',
  slug: '#',
  title: '5 помилок при наймі топ‑менеджерів та як їх уникнути',
  excerpt: 'Розбираємо типові помилки компаній при пошуку C‑level керівників та ділимося перевіреними підходами.',
  date: '8 лютого 2026',
  category: 'Стаття',
  categoryColor: 'text-amber-600',
  categoryBg: 'bg-amber-50',
  emoji: '💡',
  gradientFrom: 'from-amber-100',
  gradientTo: 'to-orange-100'
}, {
  id: '3',
  slug: '#',
  title: 'Ринок праці 2026: тренди та прогнози для роботодавців',
  excerpt: 'Аналізуємо ключові тенденції ринку праці та даємо рекомендації щодо стратегії найму на найближчий рік.',
  date: '1 лютого 2026',
  category: 'Огляд',
  categoryColor: 'text-emerald-600',
  categoryBg: 'bg-emerald-50',
  emoji: '🌍',
  gradientFrom: 'from-emerald-100',
  gradientTo: 'to-teal-100'
}, {
  id: '4',
  slug: '#',
  title: 'Як скоротити time‑to‑hire на 40%: покроковий гайд',
  excerpt: "Практичні поради та інструменти для оптимізації процесу найму — від автоматизації до структурованих інтерв'ю.",
  date: '25 січня 2026',
  category: 'Гайд',
  categoryColor: 'text-violet-600',
  categoryBg: 'bg-violet-50',
  emoji: '🎯',
  gradientFrom: 'from-violet-100',
  gradientTo: 'to-purple-100'
}, {
  id: '5',
  slug: '#',
  title: 'Кейс: підбір фінансового директора для міжнародної компанії',
  excerpt: 'Як ми знайшли CFO з досвідом IPO для компанії, що виходить на європейський ринок.',
  date: '18 січня 2026',
  category: 'Кейс',
  categoryColor: 'text-primary-600',
  categoryBg: 'bg-primary-50',
  emoji: '💼',
  gradientFrom: 'from-sky-100',
  gradientTo: 'to-primary-100'
}, {
  id: '6',
  slug: '#',
  title: 'Employer Branding: як стартапу конкурувати за таланти з корпораціями',
  excerpt: 'Стратегії побудови привабливого бренду роботодавця без великих бюджетів — реальні приклади та результати.',
  date: '10 січня 2026',
  category: 'Стаття',
  categoryColor: 'text-amber-600',
  categoryBg: 'bg-amber-50',
  emoji: '🚀',
  gradientFrom: 'from-rose-100',
  gradientTo: 'to-pink-100'
}, {
  id: '7',
  slug: '#',
  title: 'Огляд зарплат IT‑спеціалістів в Україні: Q1 2026',
  excerpt: 'Детальний аналіз ринкових зарплат для розробників, DevOps, QA та менеджерів продуктів.',
  date: '5 січня 2026',
  category: 'Огляд',
  categoryColor: 'text-emerald-600',
  categoryBg: 'bg-emerald-50',
  emoji: '📈',
  gradientFrom: 'from-teal-100',
  gradientTo: 'to-emerald-100'
}, {
  id: '8',
  slug: '#',
  title: "Як провести структуроване інтерв'ю: шаблон та чек‑лист",
  excerpt: "Готовий фреймворк для проведення ефективних співбесід, що допоможе об'єктивно оцінити кандидатів.",
  date: '28 грудня 2025',
  category: 'Гайд',
  categoryColor: 'text-violet-600',
  categoryBg: 'bg-violet-50',
  emoji: '📋',
  gradientFrom: 'from-indigo-100',
  gradientTo: 'to-violet-100'
}];
function ArticleCard({
  article,
  index
}: {
  article: Article;
  index: number;
}) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const CardWrapper = article.slug.startsWith('/') ? Link : 'a';
  const linkProps = article.slug.startsWith('/') ? {
    to: article.slug
  } : {
    href: article.slug
  };
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 24
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.4,
    delay: index % 3 * 0.08
  }} data-id="element-683">
      <CardWrapper {...linkProps as any} className="group block bg-white rounded-2xl border border-navy-100 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer" data-id="element-684">
        <div className={`aspect-[16/10] bg-gradient-to-br ${article.gradientFrom} ${article.gradientTo} flex items-center justify-center overflow-hidden`} data-id="element-685">
          <div className="w-16 h-16 rounded-2xl bg-white/40 flex items-center justify-center" data-id="element-686">
            <span className="text-3xl" data-id="element-687">{article.emoji}</span>
          </div>
        </div>
        <div className="p-6" data-id="element-688">
          <div className="flex items-center gap-2 text-sm text-navy-400 mb-3" data-id="element-689">
            <CalendarIcon className="w-4 h-4" data-id="element-690" />
            <span data-id="element-691">{article.date}</span>
            <span className={`px-2 py-0.5 ${article.categoryBg} ${article.categoryColor} rounded-md text-xs font-medium`} data-id="element-692">
              {article.category}
            </span>
          </div>
          <h3 className="text-lg font-bold text-navy-900 group-hover:text-primary-600 transition-colors leading-snug" data-id="element-693">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-navy-500 leading-relaxed" data-id="element-694">
            {article.excerpt}
          </p>
          <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all" data-id="element-695">
            Читати далі
            <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-696" />
          </span>
        </div>
      </CardWrapper>
    </motion.article>;
}
export function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, {
    once: true
  });
  const filteredArticles = articles.filter(article => {
    const matchesCategory = activeCategory === 'all' || article.category === activeCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <div className="min-h-screen w-full bg-white" data-id="element-697">
      <Header data-id="element-698" />

      <main className="pt-16 lg:pt-20" data-id="element-699">
        {/* Hero */}
        <section className="relative bg-navy-50 overflow-hidden" ref={heroRef} data-id="element-700">
          <div className="absolute inset-0" data-id="element-701">
            <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-primary-200/20 blur-3xl" data-id="element-702" />
            <div className="absolute bottom-0 left-1/4 w-[250px] h-[250px] rounded-full bg-cyan-200/15 blur-3xl" data-id="element-703" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-id="element-704">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={heroInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5
          }} className="max-w-2xl" data-id="element-705">
              <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-706">
                Блог ESCA
              </span>
              <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-707">
                Статті, кейси та інсайти
              </h1>
              <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-708">
                Ділимося досвідом, аналізуємо ринок праці та розповідаємо про
                успішні проєкти підбору персоналу
              </p>
            </motion.div>

            {/* Search */}
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={heroInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.15
          }} className="mt-8 max-w-lg" data-id="element-709">
              <div className="relative" data-id="element-710">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" data-id="element-711" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Пошук статей..." className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-navy-200 bg-white text-navy-900 placeholder-navy-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors" data-id="element-712" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Categories + Articles */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-713">
          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-10" data-id="element-714">
            {categories.map(cat => <button key={cat.value} onClick={() => setActiveCategory(cat.value)} className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${activeCategory === cat.value ? 'bg-primary-600 text-white shadow-sm shadow-primary-600/20' : 'bg-navy-100 text-navy-600 hover:bg-navy-200'}`} data-id="element-715">
                {cat.label}
              </button>)}
          </div>

          {/* Articles count */}
          <div className="flex items-center gap-2 mb-8 text-sm text-navy-400" data-id="element-716">
            <TagIcon className="w-4 h-4" data-id="element-717" />
            <span data-id="element-718">
              {filteredArticles.length}{' '}
              {filteredArticles.length === 1 ? 'стаття' : filteredArticles.length < 5 ? 'статті' : 'статей'}
            </span>
          </div>

          {/* Articles Grid */}
          {filteredArticles.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-719">
              {filteredArticles.map((article, index) => <ArticleCard key={article.id} article={article} index={index} data-id="element-720" />)}
            </div> : <div className="text-center py-20" data-id="element-721">
              <div className="w-16 h-16 rounded-2xl bg-navy-100 flex items-center justify-center mx-auto mb-4" data-id="element-722">
                <SearchIcon className="w-7 h-7 text-navy-400" data-id="element-723" />
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-724">
                Нічого не знайдено
              </h3>
              <p className="text-navy-500" data-id="element-725">
                Спробуйте змінити фільтр або пошуковий запит
              </p>
            </div>}
        </section>

        {/* Newsletter CTA */}
        <section className="bg-navy-50/50 py-16" data-id="element-726">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-727">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-navy-900 tracking-tight" data-id="element-728">
              Підпишіться на наші інсайти
            </h2>
            <p className="mt-3 text-navy-500" data-id="element-729">
              Отримуйте найкращі статті про рекрутинг та HR прямо на пошту — раз
              на тиждень
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-id="element-730">
              <input type="email" placeholder="your@email.com" className="flex-1 px-4 py-3 rounded-xl border border-navy-200 bg-white text-navy-900 placeholder-navy-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500" data-id="element-731" />
              <button className="px-6 py-3 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors shadow-sm" data-id="element-732">
                Підписатися
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-733" />
    </div>;
}